import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Trash2, RotateCcw, Eye, Search, Calendar, Database, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../api/api';

const DeleteLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  
  // Filters
  const [selectedCollection, setSelectedCollection] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/delete-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await apiClient.get('/delete-logs/collections');
      setCollections(response.data);
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      let url = `/delete-logs/?page=${page}&limit=20`;
      
      if (selectedCollection && selectedCollection !== 'all') {
        url += `&collection_name=${selectedCollection}`;
      }
      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }
      
      const response = await apiClient.get(url);
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch delete logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCollections();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, selectedCollection, startDate, endDate]);

  const handleViewDetail = async (logId) => {
    try {
      const response = await apiClient.get(`/delete-logs/${logId}`);
      setSelectedLog(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to fetch log details');
    }
  };

  const handleRestore = async () => {
    if (!selectedLog) return;
    
    try {
      await apiClient.post(`/delete-logs/${selectedLog.id}/restore`);
      toast.success('Item restored successfully');
      setShowRestoreDialog(false);
      setShowDetailDialog(false);
      fetchLogs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore item');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCollectionLabel = (name) => {
    const labels = {
      'students': 'Students',
      'staff': 'Staff',
      'online_admission_applications': 'Online Applications',
      'seat_bookings': 'Seat Bookings',
      'incomes': 'Income',
      'expenses': 'Expense',
      'fees': 'Fees',
      'books': 'Library Books',
      'vehicles': 'Vehicles',
      'hostel_students': 'Hostel Students'
    };
    return labels[name] || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6 p-6" data-testid="delete-logs-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Delete Logs</h1>
          <p className="text-gray-500">Track and restore deleted items across the system</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">Total Deletions</p>
                  <p className="text-3xl font-bold">{stats.total_deletions}</p>
                </div>
                <Trash2 className="h-10 w-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Today</p>
                  <p className="text-3xl font-bold">{stats.today}</p>
                </div>
                <Calendar className="h-10 w-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Last 7 Days</p>
                  <p className="text-3xl font-bold">{stats.last_7_days}</p>
                </div>
                <Database className="h-10 w-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Restored</p>
                  <p className="text-3xl font-bold">{stats.restored}</p>
                </div>
                <RotateCcw className="h-10 w-10 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Collection</label>
              <Select value={selectedCollection} onValueChange={setSelectedCollection}>
                <SelectTrigger data-testid="collection-filter">
                  <SelectValue placeholder="All Collections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Collections</SelectItem>
                  {collections.map(col => (
                    <SelectItem key={col} value={col}>{getCollectionLabel(col)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="min-w-[150px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-filter"
              />
            </div>
            
            <div className="min-w-[150px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-filter"
              />
            </div>
            
            <Button
              variant="outline"
              onClick={() => {
                setSelectedCollection('all');
                setStartDate('');
                setEndDate('');
                setPage(1);
              }}
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Delete History</CardTitle>
          <CardDescription>View all deleted items and restore if needed</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Trash2 className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>No delete logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Collection</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Item ID</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Deleted By</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Deleted At</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50" data-testid={`log-row-${log.id}`}>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{getCollectionLabel(log.collection_name)}</Badge>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-gray-600">
                        {log.deleted_item_id?.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{log.deleted_by?.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{log.deleted_by?.role}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(log.deleted_at)}
                      </td>
                      <td className="px-4 py-3">
                        {log.restored ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Restored
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Deleted
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetail(log.id)}
                            data-testid={`view-btn-${log.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!log.restored && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => {
                                setSelectedLog(log);
                                setShowRestoreDialog(true);
                              }}
                              data-testid={`restore-btn-${log.id}`}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <span className="py-2 px-4 text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delete Log Details</DialogTitle>
            <DialogDescription>
              Full details of the deleted item
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Collection</label>
                  <p className="font-medium">{getCollectionLabel(selectedLog.collection_name)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Item ID</label>
                  <p className="font-mono text-sm">{selectedLog.deleted_item_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Deleted By</label>
                  <p>{selectedLog.deleted_by?.name} ({selectedLog.deleted_by?.role})</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Deleted At</label>
                  <p>{formatDate(selectedLog.deleted_at)}</p>
                </div>
                {selectedLog.reason && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-500">Reason</label>
                    <p>{selectedLog.reason}</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500 mb-2 block">Deleted Data</label>
                <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                  {JSON.stringify(selectedLog.deleted_item_data, null, 2)}
                </pre>
              </div>
              
              {selectedLog.restored && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-700 font-medium">
                    Restored on {formatDate(selectedLog.restored_at)} by {selectedLog.restored_by?.name}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {selectedLog && !selectedLog.restored && (
              <Button
                onClick={() => setShowRestoreDialog(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Restore Item
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Restore</DialogTitle>
            <DialogDescription>
              Are you sure you want to restore this item? It will be added back to the {selectedLog?.collection_name} collection.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRestore} className="bg-green-600 hover:bg-green-700">
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeleteLogs;
